import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, API, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useRecharge } from '@/components/RechargeProvider';
import { fetchRegion, formatPrice } from '@/lib/region';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import ProfilePicker from '@/components/ProfilePicker';
import { toast } from '@/lib/toast';
import { Heart, Loader2, Sparkles, Download, Share2, ArrowLeft, Coins, ThumbsUp, ThumbsDown, Check } from 'lucide-react';

// Round 47 — Price comes from /api/pricing/compatibility (DB-backed,
// admin-editable). Hardcoded values below are only the bootstrap
// fallback for the first render before the API responds.
const FALLBACK_LIST  = { INR: 500, USD: 25 };
const FALLBACK_FINAL = { INR: 300, USD: 15 };

/**
 * A87 — KP Compatibility Matching screen.
 *
 * 3 steps: Form → Processing → Result.
 * Entry points: Profile page button + Ask suggested chip (with ♡ icon).
 */
export default function Compatibility() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { open: openRecharge } = useRecharge();

    const [step, setStep] = useState('form'); // 'form' | 'processing' | 'result'
    const [profileId, setProfileId] = useState(null);
    const [profileObj, setProfileObj] = useState(null);

    const [partner, setPartner] = useState({
        name: '', date: '', time: '', place: '', lat: null, lon: null, tz: null,
    });
    const [placeText, setPlaceText] = useState('');

    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Region-aware cost — fetched from the DB-backed pricing endpoint.
    // Falls back to hardcoded `FALLBACK_*` for the first render only.
    const [currency, setCurrency] = useState('INR');
    const [priceListAmt, setPriceListAmt]   = useState(null);   // null → loading
    const [priceFinalAmt, setPriceFinalAmt] = useState(null);
    const [discountPct, setDiscountPct]     = useState(0);
    useEffect(() => {
        let alive = true;
        fetchRegion()
            .then((r) => { if (alive) setCurrency(r?.currency || 'INR'); })
            .catch(() => { /* default INR */ });
        api.get('/pricing/compatibility')
            .then(({ data }) => {
                if (!alive) return;
                setCurrency(data.currency || 'INR');
                setPriceListAmt(Number(data.list_amount));
                setPriceFinalAmt(Number(data.final_amount));
                setDiscountPct(Number(data.discount_pct || 0));
            })
            .catch(() => { /* fallback in place */ });
        return () => { alive = false; };
    }, []);
    const listAmount  = priceListAmt  ?? FALLBACK_LIST[currency]  ?? FALLBACK_LIST.INR;
    const finalAmount = priceFinalAmt ?? FALLBACK_FINAL[currency] ?? FALLBACK_FINAL.INR;
    const costAmount  = finalAmount;
    const costLabel   = formatPrice(costAmount, currency);
    const listLabel   = formatPrice(listAmount, currency);
    const showDiscount = discountPct > 0 && listAmount > finalAmount;

    // Allow deep-link to a pre-existing reading: /app/compatibility?check_id=…
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const checkId = params.get('check_id');
        if (checkId) {
            api.get(`/compatibility/${checkId}`)
                .then(({ data }) => { setResult(data); setStep('result'); })
                .catch((e) => toast.error(readErr(e, 'Could not load reading')));
        }
    }, [location.search]);

    // Round 43b — Pull wallet balance from /wallet/balance (single source
    // of truth used by AppShell). Previously we read user.wallet_balance_inr
    // off the auth store which was stale for USD users and showed $0 in the
    // CTA footer even when the AppShell header pill showed the correct
    // figure. Refresh on mount + after any successful POST below.
    const [walletInr, setWalletInr] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/wallet/balance');
                if (alive) setWalletInr(Number(data?.balance_inr ?? 0));
            } catch { /* not logged in or wallet endpoint not ready */ }
        })();
        return () => { alive = false; };
    }, [result?.id]);   // refetch after a reading completes

    const balance = walletInr ?? Number(user?.wallet_balance_inr ?? 0);
    const insufficient = balance < costAmount;

    const canSubmit = useMemo(() => (
        partner.name.trim().length > 0
        && /^\d{4}-\d{2}-\d{2}$/.test(partner.date)
        && partner.place
        && partner.lat != null && partner.lon != null
        && !insufficient
    ), [partner, insufficient]);

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setStep('processing');
        try {
            const { data } = await api.post('/compatibility', {
                profile_id: profileId,
                partner_name: partner.name.trim(),
                partner_birth_date: partner.date,
                partner_birth_time: partner.time || null,
                partner_birth_place: partner.place,
                partner_lat: partner.lat,
                partner_lon: partner.lon,
                partner_timezone: partner.tz || 'UTC',
            });
            setResult(data);
            setStep('result');
            await refresh?.();
        } catch (e) {
            setStep('form');
            toast.error(readErr(e, 'Could not run compatibility'));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setResult(null);
        setPartner({ name: '', date: '', time: '', place: '', lat: null, lon: null, tz: null });
        setPlaceText('');
        setStep('form');
    };

    if (step === 'processing') {
        return <ProcessingScreen />;
    }
    if (step === 'result' && result) {
        return <ResultScreen result={result} onNew={reset} />;
    }

    return (
        <div className="max-w-3xl mx-auto px-6 py-10 md:py-16 space-y-8" data-testid="compatibility-page">
            <div>
                <div className="overline mb-3">Compatibility reading</div>
                <h1 className="font-serif text-4xl md:text-5xl text-parchment">
                    Are these two charts compatible?
                </h1>
                <p className="text-sm text-slate-400 mt-3 flex flex-wrap items-center gap-2" data-testid="compat-price-strip">
                    {showDiscount ? (
                        <>
                            <span className="line-through text-slate-500" data-testid="compat-list-price">{listLabel}</span>
                            <span className="text-marigold font-semibold" data-testid="compat-final-price">{costLabel}</span>
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold uppercase tracking-wider"
                                data-testid="compat-discount-badge"
                            >
                                {discountPct}% OFF
                            </span>
                        </>
                    ) : (
                        <span data-testid="compat-final-price">{costLabel}</span>
                    )}
                    <span className="text-slate-600">·</span>
                    <span>Your birth details are already on file</span>
                </p>
            </div>

            {/* YOUR CHART panel */}
            <Card className="bg-elevated border-marigold/15 rounded-sm p-6" data-testid="compat-your-chart">
                <div className="overline mb-2">Your chart</div>
                <ProfilePicker
                    value={profileId}
                    onChange={(id, p) => { setProfileId(id); setProfileObj(p); }}
                    testIdPrefix="compat-profile"
                />
                {profileObj?.birth_details && (
                    <div className="mt-3 text-xs text-slate-400 leading-relaxed">
                        {profileObj.name}
                        {' · '}{profileObj.birth_details.date}
                        {profileObj.birth_details.time ? ` · ${profileObj.birth_details.time}` : ''}
                        {profileObj.birth_details.place ? ` · ${profileObj.birth_details.place}` : ''}
                    </div>
                )}
            </Card>

            {/* PARTNER form */}
            <Card className="bg-elevated border-marigold/15 rounded-sm p-6" data-testid="compat-partner-form">
                <div className="flex items-center gap-2 mb-5">
                    <Heart className="text-marigold" size={18} />
                    <h2 className="font-serif text-xl text-parchment">Partner&apos;s details</h2>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Partner&apos;s name</Label>
                        <Input
                            type="text"
                            value={partner.name}
                            onChange={(e) => setPartner({ ...partner, name: e.target.value })}
                            className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11"
                            placeholder="Full name"
                            data-testid="compat-partner-name"
                        />
                    </div>

                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Date of birth</Label>
                        <Input
                            type="date"
                            value={partner.date}
                            onChange={(e) => setPartner({ ...partner, date: e.target.value })}
                            className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11"
                            data-testid="compat-partner-date"
                        />
                    </div>

                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Time of birth (optional)</Label>
                        <Input
                            type="time"
                            value={partner.time}
                            onChange={(e) => setPartner({ ...partner, time: e.target.value })}
                            className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11"
                            data-testid="compat-partner-time"
                        />
                        {!partner.time && (
                            <div className="mt-2 text-xs text-slate-500"
                                 data-testid="compat-partner-time-note">
                                Birth time not provided — analysis uses noon as default.
                                For greater accuracy please provide the birth time.
                            </div>
                        )}
                    </div>

                    <div className="sm:col-span-2">
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Place of birth</Label>
                        <div className="mt-2">
                            <PlaceAutocomplete
                                value={placeText}
                                onTextChange={setPlaceText}
                                onSelect={(loc) => {
                                    setPlaceText(loc.place);
                                    setPartner({ ...partner, place: loc.place, lat: loc.lat, lon: loc.lon, tz: loc.tz });
                                }}
                                testId="compat-partner-place"
                            />
                        </div>
                        {partner.place && partner.lat != null && (
                            <div className="mt-2 text-xs text-marigold/80" data-testid="compat-partner-place-coords">
                                {partner.lat}, {partner.lon} · {partner.tz}
                            </div>
                        )}
                    </div>
                </div>

                <Button
                    onClick={submit}
                    disabled={!canSubmit || submitting}
                    aria-disabled={!canSubmit || submitting}
                    data-testid="compat-submit"
                    className="mt-6 w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm h-12 text-base font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:bg-marigold"
                >
                    {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />}
                    {showDiscount ? (
                        <span className="inline-flex items-center gap-2">
                            Check compatibility —
                            <span className="line-through opacity-70 text-sm">{listLabel}</span>
                            <span className="font-semibold">{costLabel}</span>
                            <span className="px-1.5 py-0.5 rounded-sm bg-obsidian/25 text-[10px] font-bold tracking-wider">{discountPct}% OFF</span>
                        </span>
                    ) : (
                        <>Check compatibility — {costLabel}</>
                    )}
                </Button>

                <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-slate-500 flex items-center gap-1">
                        <Coins size={12} className="text-marigold" />
                        Wallet: <span className="text-parchment ml-0.5" data-testid="compat-balance">{formatPrice(balance, currency)}</span>
                    </span>
                    {insufficient && (
                        <button
                           type="button"
                           onClick={() => openRecharge({
                               onSuccess: () => {
                                   // After payment lands, refresh the wallet
                                   // balance in the auth store so the `insufficient`
                                   // flag immediately re-evaluates and the user
                                   // can submit without leaving the page.
                                   if (typeof refresh === 'function') refresh();
                               },
                           })}
                           className="text-marigold underline hover:text-marigold-hover"
                           data-testid="compat-topup-link">
                            You need {costLabel} — top up
                        </button>
                    )}
                </div>
            </Card>
        </div>
    );
}


function ProcessingScreen() {
    // Round 46 — animated multi-stage progress bar.
    //
    // The backend takes ~15–30s for a v2 reading (engine compute + Claude
    // scribe). We have no real progress events, so we simulate a steady
    // ease-out curve toward ~95% over 30s and pair it with phase labels
    // that hint at what's happening behind the scenes. When the parent
    // unmounts (reading is done), this whole component disappears.
    const PHASES = [
        { pct: 12, label: 'Casting both birth charts',           hint: 'Locating ascendants, computing sub-lords…' },
        { pct: 28, label: 'Reading cuspal sub-lords',            hint: 'Checking each layer of the KP 249 table…' },
        { pct: 48, label: 'Analysing star-level resonance',      hint: '7th lord, 5th lord, karmic crossings…' },
        { pct: 68, label: 'Weighing strengths & frictions',      hint: 'Bilateral ruling planets, navamsa cross-check…' },
        { pct: 84, label: 'Composing your narration',            hint: "Guruji's scribe is writing the five layers…" },
        { pct: 95, label: 'Finalising the verdict',              hint: 'Almost there — a few more seconds…' },
    ];

    const [pct, setPct] = useState(4);
    const [phaseIdx, setPhaseIdx] = useState(0);

    useEffect(() => {
        // Smooth ease-out from 4% to ~95% over ~30 seconds.
        let raf;
        const start = performance.now();
        const TARGET_MS = 30000;
        const tick = (now) => {
            const elapsed = now - start;
            // ease-out cubic → fast early, slow near the end
            const t = Math.min(elapsed / TARGET_MS, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            const value = 4 + eased * (95 - 4);
            setPct(value);
            // Update phase index from current pct
            const idx = PHASES.findIndex((p) => value < p.pct);
            setPhaseIdx(idx === -1 ? PHASES.length - 1 : Math.max(0, idx));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    const current = PHASES[phaseIdx];

    return (
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-24" data-testid="compat-processing">
            <div className="text-center mb-10">
                <Loader2 className="animate-spin text-marigold mx-auto mb-6" size={36} />
                <div className="font-serif text-2xl md:text-3xl text-parchment">
                    Analysing both charts…
                </div>
                <p className="text-sm text-slate-400 mt-3 max-w-md mx-auto">
                    A full KP compatibility reading runs across five doctrinal layers.
                    This usually takes 20–40 seconds.
                </p>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
                <div className="flex items-baseline justify-between mb-2">
                    <div className="overline text-marigold/80" data-testid="compat-progress-phase">
                        {current.label}
                    </div>
                    <div className="text-xs tabular-nums text-slate-400" data-testid="compat-progress-pct">
                        {Math.round(pct)}%
                    </div>
                </div>
                <div
                    className="h-2 w-full rounded-full bg-marigold/10 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={Math.round(pct)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    data-testid="compat-progress-bar"
                >
                    {/* Round 47b — Removed the CSS `transition-[width]
                        duration-700`. The per-frame JS animation
                        (requestAnimationFrame) was being capped by it:
                        each 16ms width update restarted the 700ms CSS
                        interpolation, so the bar visibly lagged ~90%
                        behind the percentage text. JS already gives us
                        a smooth 60fps update; no CSS transition needed. */}
                    <div
                        className="h-full bg-gradient-to-r from-marigold/70 via-marigold to-amber-300 rounded-full"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <p className="text-xs text-slate-500 mt-3" data-testid="compat-progress-hint">
                    {current.hint}
                </p>
            </div>

            {/* Phase checklist */}
            <Card className="bg-elevated border-marigold/10 rounded-sm p-5 mt-8">
                <ul className="space-y-2.5">
                    {PHASES.slice(0, -1).map((p, i) => {
                        const done = i < phaseIdx;
                        const active = i === phaseIdx;
                        return (
                            <li
                                key={p.label}
                                className={`flex items-center gap-3 text-sm transition-colors ${done ? 'text-emerald-400' : active ? 'text-parchment' : 'text-slate-500'}`}
                                data-testid={`compat-progress-step-${i}`}
                            >
                                <span
                                    className={`inline-flex items-center justify-center h-5 w-5 rounded-full border ${done ? 'border-emerald-500/60 bg-emerald-500/10' : active ? 'border-marigold/60 bg-marigold/10' : 'border-slate-700 bg-slate-800/40'}`}
                                >
                                    {done
                                        ? <Check size={12} className="text-emerald-400" />
                                        : active
                                            ? <Loader2 size={12} className="animate-spin text-marigold" />
                                            : null}
                                </span>
                                <span>{p.label}</span>
                            </li>
                        );
                    })}
                </ul>
            </Card>
        </div>
    );
}


function ResultScreen({ result, onNew }) {
    const navigate = useNavigate();
    const verdict = (result.verdict || 'uncertain').toLowerCase();
    const a = result.result?.person_a_name || result.profile_name || 'You';
    const b = result.result?.person_b_name || result.partner_name || 'Partner';
    const narration = result.narration || '';

    const VERDICT = {
        highly_compatible: { label: 'Highly compatible', icon: '✦', tone: 'bg-marigold/15 border-marigold/60 text-marigold' },
        compatible:        { label: 'Compatible',        icon: '✓', tone: 'bg-marigold/10 border-marigold/40 text-marigold' },
        partially_compatible: { label: 'Partially compatible', icon: '~', tone: 'bg-amber-500/10 border-amber-500/40 text-amber-300' },
        uncertain:         { label: 'Uncertain',         icon: '?', tone: 'bg-slate-500/10 border-slate-500/40 text-slate-300' },
        incompatible:      { label: 'Incompatible',      icon: '✗', tone: 'bg-red-900/30 border-red-700/60 text-red-300' },
    }[verdict] || { label: verdict, icon: '?', tone: 'bg-slate-500/10 border-slate-500/40 text-slate-300' };

    const showCTA = verdict === 'incompatible' || verdict === 'partially_compatible' || verdict === 'uncertain';
    const cardUrl = `${API}/compatibility/${result.id}/card`;
    const token = localStorage.getItem('ac_token');

    const downloadCard = async () => {
        try {
            const resp = await fetch(cardUrl, { headers: { Authorization: `Bearer ${token}` } });
            if (!resp.ok) throw new Error('Could not generate image');
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `astroconsult-${a}-${b}.png`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(readErr(e, 'Could not download image'));
        }
    };

    const shareCard = async () => {
        if (!navigator.share) {
            return downloadCard();
        }
        try {
            const resp = await fetch(cardUrl, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await resp.blob();
            const file = new File([blob], `astroconsult-${a}-${b}.png`, { type: 'image/png' });
            await navigator.share({
                title: `${a} & ${b} — Compatibility reading`,
                text: `${VERDICT.label} — via AstroConsult`,
                files: [file],
            });
        } catch (e) {
            if (e?.name !== 'AbortError') {
                toast.error('Could not share — image downloaded instead');
                downloadCard();
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-10 md:py-16 space-y-8" data-testid="compat-result">
            <button
                onClick={() => navigate('/app/profile')}
                className="text-sm text-slate-400 hover:text-marigold flex items-center gap-1"
                data-testid="compat-result-back"
            >
                <ArrowLeft size={14} /> Back to profile
            </button>

            {/* Round 43f — Removed the big hero VERDICT card. The verdict
                is already shown inside <CompatNarrationV2> (centred,
                medium-sized) and again on the shareable summary card
                below, so the giant hero was a duplicate. */}

            <CompatNarration narration={narration} verdictLabel={VERDICT.label} />

            {/* Round 43d — Replaced the PNG-based shareable image with a
                simple HTML/CSS summary card: overall verdict at the top, 5
                doctrine layers below with a 1-line takeaway + thumb icon
                per layer. Renders only when v2 narration is present. */}
            {(narration && typeof narration === 'object' && narration.schema_version === 2)
                ? <CompatSummaryCard narration={narration} personA={result.profile_name || 'You'} personB={result.partner_name || 'Partner'} checkId={result.id} />
                : null}

            {/* Consultation CTA */}
            {showCTA && (
                <Card className="bg-surface border-marigold/30 rounded-sm p-6" data-testid="compat-consult-cta">
                    <p className="text-parchment mb-4">
                        For a deeper exploration of what this means for your relationship,
                        book a consultation with one of our astrologers.
                    </p>
                    <Button
                        onClick={() => navigate('/app/consult')}
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm h-11 px-6"
                        data-testid="compat-consult-btn"
                    >
                        Book a consultation
                    </Button>
                </Card>
            )}

            {/* Round 43d — Legacy "Share your result" PNG-download block
                removed. The new <CompatSummaryCard> above carries its own
                Download + WhatsApp Share buttons. */}

            <div className="text-center">
                <button
                    onClick={onNew}
                    className="text-sm text-marigold hover:text-marigold-hover underline"
                    data-testid="compat-new-reading"
                >
                    Run another compatibility reading
                </button>
            </div>
        </div>
    );
}


function CheckCard({ title, status, bar, twoLines, line, testId }) {
    return (
        <Card className="bg-elevated border-marigold/15 rounded-sm p-5" data-testid={testId}>
            <div className="overline mb-2">{title}</div>
            {status && (
                <div className={`text-2xl font-medium mb-2 ${status === 'pass' ? 'text-marigold' : 'text-red-400'}`}
                     data-testid={`${testId}-status`}>
                    {status === 'pass' ? '✓ Clear' : '✗ Blocked'}
                </div>
            )}
            {bar && (
                <div className="mb-3" data-testid={`${testId}-strength-${bar}`}>
                    <div className="flex gap-1">
                        <div className={`h-2 flex-1 rounded-sm ${bar === 'strong' || bar === 'moderate' || bar === 'weak' ? 'bg-marigold' : 'bg-slate-700'}`} />
                        <div className={`h-2 flex-1 rounded-sm ${bar === 'strong' || bar === 'moderate' ? 'bg-marigold' : 'bg-slate-700'}`} />
                        <div className={`h-2 flex-1 rounded-sm ${bar === 'strong' ? 'bg-marigold' : 'bg-slate-700'}`} />
                    </div>
                    <div className="text-sm text-marigold mt-2 capitalize">{bar}</div>
                </div>
            )}
            {twoLines && (
                <div className="space-y-1" data-testid={`${testId}-lines`}>
                    <div className="text-sm text-parchment">{twoLines.a}</div>
                    <div className="text-sm text-parchment">{twoLines.b}</div>
                </div>
            )}
            {line && (
                <p className="text-xs text-slate-400 leading-relaxed mt-1">{line}</p>
            )}
        </Card>
    );
}


function humanQuality(q) {
    return ({
        harmonious:        'harmonious',
        mostly_harmonious: 'mostly harmonious',
        mixed:             'mixed',
        challenging:       'challenging',
    }[q] || 'mixed');
}


/**
 * "Why this verdict?" — deterministic, structured per-check reasoning.
 * Backend ships `detailed_reasons = { summary, checks: [...] }` with
 * every reading. We render it as a clean numbered list so the user can
 * see exactly which planets/houses/nakshatras drove the outcome.
 */
function DetailedReasons({ reasons }) {
    if (!reasons || !Array.isArray(reasons.checks) || reasons.checks.length === 0) {
        return null;
    }
    return (
        <Card className="bg-elevated border-marigold/15 rounded-sm p-6 md:p-8 space-y-6"
              data-testid="compat-detailed-reasons">
            <div>
                <div className="overline mb-3">Why this verdict?</div>
                <h3 className="font-serif text-2xl text-parchment mb-3">The reasoning behind your result</h3>
                {reasons.summary && (
                    <p className="text-parchment leading-relaxed"
                       data-testid="compat-reasons-summary">
                        {reasons.summary}
                    </p>
                )}
            </div>

            <div className="space-y-5">
                {reasons.checks.map((c, idx) => (
                    <ReasonBlock key={c.id || idx} reason={c} index={idx + 1} />
                ))}
            </div>
        </Card>
    );
}


function ReasonBlock({ reason, index }) {
    const tone = ({
        pass:               'text-marigold',
        strong:             'text-marigold',
        moderate:           'text-amber-300',
        harmonious:         'text-marigold',
        mostly_harmonious:  'text-marigold',
        mixed:              'text-slate-300',
        weak:               'text-slate-400',
        fail:               'text-red-300',
        challenging:        'text-red-300',
    }[reason.status]) || 'text-slate-300';
    const label = humanStatus(reason.status);
    return (
        <div className="border-l-2 border-marigold/30 pl-5"
             data-testid={`compat-reason-${reason.id}`}>
            <div className="flex items-baseline gap-3 flex-wrap mb-1.5">
                <span className="text-xs text-slate-500 font-mono tabular-nums">{String(index).padStart(2, '0')}.</span>
                <span className="font-serif text-lg text-parchment">{reason.title}</span>
                {label && (
                    <span className={`text-xs uppercase tracking-wider ${tone}`}
                          data-testid={`compat-reason-${reason.id}-status`}>
                        {label}
                    </span>
                )}
            </div>
            <p className="text-sm text-parchment leading-relaxed mb-2 italic">
                {reason.headline}
            </p>
            {Array.isArray(reason.details) && reason.details.length > 0 && (
                <ul className="space-y-1.5 mb-2 list-disc list-outside ml-5"
                    data-testid={`compat-reason-${reason.id}-details`}>
                    {reason.details.map((d, i) => (
                        <li key={i} className="text-sm text-slate-300 leading-relaxed">{d}</li>
                    ))}
                </ul>
            )}
            {reason.implication && (
                <p className="text-sm text-slate-400 leading-relaxed">
                    {reason.implication}
                </p>
            )}
        </div>
    );
}


function humanStatus(s) {
    return ({
        pass:               'Clear',
        fail:               'Friction',
        strong:             'Strong',
        moderate:           'Moderate',
        weak:               'Weak',
        harmonious:         'Harmonious',
        mostly_harmonious:  'Mostly harmonious',
        mixed:              'Mixed',
        challenging:        'Challenging',
    }[s]) || (s ? String(s).replace(/_/g, ' ') : '');
}

// Round 40d — three-section narration renderer. Accepts the new
// {verdict, sections: {what_works, what_needs_attention, a_word_of_guidance}}
// envelope from narrate_compatibility(). Falls back to a single-card
// render when the field is a plain string (legacy rows in the DB).
//
// Round 43 — when `narration.schema_version === 2`, render the new 5-card
// + 3-section layout (cards: card01..card05 with pass/mixed/friction icons,
// followed by what_works / what_needs_attention / a_word_of_guidance).
function CompatNarration({ narration, verdictLabel }) {
    if (!narration) return null;

    // Legacy shape — narration was a single string.
    if (typeof narration === 'string') {
        return (
            <Card className="bg-elevated border-marigold/15 rounded-sm p-6 md:p-8"
                  data-testid="compat-narration">
                <p className="text-parchment leading-relaxed whitespace-pre-wrap">{narration}</p>
            </Card>
        );
    }

    // Round 43 — v2 schema (5 cards + 3 narration sections)
    if (narration.schema_version === 2 && narration.cards) {
        return <CompatNarrationV2 narration={narration} />;
    }

    const sections = narration.sections || {};
    const sectionLabel = verdictLabel || narration.verdict;
    const SECTIONS = [
        { key: 'what_works',           title: 'What works between you' },
        { key: 'what_needs_attention', title: 'What needs attention' },
        { key: 'a_word_of_guidance',   title: 'A Word of Guidance' },
    ];

    return (
        <div className="space-y-4" data-testid="compat-narration">
            {sectionLabel && (
                <div className="overline text-marigold" data-testid="compat-narration-verdict">
                    {sectionLabel}
                </div>
            )}
            {SECTIONS.map(({ key, title }) => {
                const body = (sections[key] || '').trim();
                if (!body) return null;
                return (
                    <Card
                        key={key}
                        className="bg-elevated border-marigold/15 rounded-sm p-6 md:p-7"
                        data-testid={`compat-narration-${key.replace(/_/g, '-')}`}
                    >
                        <div className="font-serif text-xl text-marigold mb-2">{title}</div>
                        <p className="text-parchment leading-relaxed whitespace-pre-wrap">{body}</p>
                    </Card>
                );
            })}
            {narration.birth_time_note && (
                <div className="text-xs text-slate-500 italic px-2" data-testid="compat-narration-birth-time-note">
                    {narration.birth_time_note}
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Round 43 — Compatibility Narration v2 (5 cards + 3 sections)
// ─────────────────────────────────────────────────────────────────────
function CompatIconBadge({ icon }) {
    // Round 43c — Thumb icons replace the Strong/Nuanced/Attention pills.
    //  pass     → thumbs-up    (emerald)
    //  mixed    → thumb sideways (rotated, amber/orange)
    //  friction → thumbs-down  (rose)
    const cfg = {
        pass: {
            label: 'Yes',
            ring:  'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
            transform: '',
            Icon: ThumbsUp,
        },
        mixed: {
            label: 'Mixed',
            ring:  'bg-amber-500/15 border-amber-500/40 text-amber-300',
            transform: 'rotate-90',     // sideways thumb
            Icon: ThumbsUp,
        },
        friction: {
            label: 'Attention',
            ring:  'bg-rose-500/15 border-rose-500/40 text-rose-300',
            transform: '',
            Icon: ThumbsDown,
        },
    }[icon] || {
        label: '—',
        ring:  'bg-slate-500/10 border-slate-500/30 text-slate-300',
        transform: '',
        Icon: ThumbsUp,
    };
    const Ico = cfg.Icon;
    return (
        <span
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${cfg.ring}`}
            title={cfg.label}
            aria-label={cfg.label}
            data-testid={`compat-card-icon-${icon}`}
        >
            <Ico size={18} className={cfg.transform} strokeWidth={2.2} />
        </span>
    );
}

function CompatCardV2({ id, card, partnerNameA, partnerNameB }) {
    if (!card) return null;
    return (
        <Card
            className="bg-elevated border-marigold/15 rounded-sm p-5 md:p-6 space-y-3"
            data-testid={`compat-v2-${id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="font-serif text-lg md:text-xl text-marigold">{card.title}</div>
                <CompatIconBadge icon={card.icon} />
            </div>
            <p className="text-parchment/95 leading-relaxed text-sm md:text-base">{card.description}</p>
            {card.partner_nature && (card.partner_nature.person_a_sees || card.partner_nature.person_b_sees) && (
                <div className="mt-3 pt-3 border-t border-marigold/10 space-y-2 text-sm">
                    {card.partner_nature.person_a_sees && (
                        <div className="flex gap-2" data-testid={`${id}-partner-a-sees`}>
                            <span className="text-marigold/70 font-medium shrink-0">{partnerNameA || 'You'} sees:</span>
                            <span className="text-parchment/90">{card.partner_nature.person_a_sees}</span>
                        </div>
                    )}
                    {card.partner_nature.person_b_sees && (
                        <div className="flex gap-2" data-testid={`${id}-partner-b-sees`}>
                            <span className="text-marigold/70 font-medium shrink-0">{partnerNameB || 'Partner'} sees:</span>
                            <span className="text-parchment/90">{card.partner_nature.person_b_sees}</span>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}

function CompatNarrationV2({ narration }) {
    const cards = narration.cards || {};
    const narrText = narration.narration || {};
    const verdictLabel = narration.verdict_label;
    const partnerNameA = narration.person_a_name;
    const partnerNameB = narration.person_b_name;

    const SECTIONS = [
        { key: 'what_works',           title: 'What works between you' },
        { key: 'what_needs_attention', title: 'What needs attention' },
        { key: 'a_word_of_guidance',   title: 'A word of guidance' },
    ];

    return (
        <div className="space-y-6" data-testid="compat-narration-v2">
            {verdictLabel && (
                <div className="text-center" data-testid="compat-v2-verdict-label">
                    <div className="overline text-marigold/80 mb-1">Verdict</div>
                    <div className="font-serif text-2xl md:text-3xl text-parchment">{verdictLabel}</div>
                </div>
            )}

            {/* 5 cards */}
            <div className="grid md:grid-cols-2 gap-4" data-testid="compat-v2-cards">
                <CompatCardV2 id="card01" card={cards.card01} partnerNameA={partnerNameA} partnerNameB={partnerNameB} />
                <CompatCardV2 id="card02" card={cards.card02} />
                <CompatCardV2 id="card03" card={cards.card03} />
                <CompatCardV2 id="card04" card={cards.card04} />
                <CompatCardV2 id="card05" card={cards.card05} />
            </div>

            {/* 3 narration sections */}
            <div className="space-y-3 pt-3" data-testid="compat-v2-narration-sections">
                {SECTIONS.map(({ key, title }) => {
                    const body = (narrText[key] || '').trim();
                    if (!body) return null;
                    return (
                        <Card
                            key={key}
                            className="bg-elevated border-marigold/15 rounded-sm p-6 md:p-7"
                            data-testid={`compat-v2-section-${key.replace(/_/g, '-')}`}
                        >
                            <div className="font-serif text-xl text-marigold mb-2">{title}</div>
                            <p className="text-parchment leading-relaxed whitespace-pre-wrap">{body}</p>
                        </Card>
                    );
                })}
            </div>

            {narration.birth_time_note && (
                <div
                    className="text-xs text-slate-500 italic px-2"
                    data-testid="compat-v2-birth-time-note"
                >
                    {narration.birth_time_note}
                </div>
            )}
        </div>
    );
}




// ─────────────────────────────────────────────────────────────────────
// Round 43d — Summary card: verdict + 5 layers + 1-line takeaways +
//             thumb icons. Pure HTML/Tailwind. Download = text export,
//             Share = native Web Share API with WhatsApp fallback.
// Round 43e — Use full LLM-provided titles ("Core Connection", "The
//             Deeper Bond", etc.) and render the COMPLETE description
//             rather than truncating at the first sentence (the LLM is
//             already capped to 2-3 sentences per card).
// ─────────────────────────────────────────────────────────────────────
const LAYER_DEFAULTS = [
    { id: 'card01', title: 'Core Connection' },
    { id: 'card02', title: 'The Deeper Bond' },
    { id: 'card03', title: 'Karmic Connection' },
    { id: 'card04', title: 'Individual Harmony' },
    { id: 'card05', title: 'Children and Family' },
];

function _verdictTone(label) {
    return {
        'Made for Each Other':     'border-emerald-500/40 bg-emerald-500/5 text-emerald-200',
        'A Good Match':            'border-marigold/40 bg-marigold/5 text-marigold',
        'Has Its Challenges':      'border-amber-500/40 bg-amber-500/5 text-amber-200',
        'Needs Careful Thought':   'border-rose-500/40 bg-rose-500/5 text-rose-200',
    }[label] || 'border-marigold/30 bg-surface text-parchment';
}

function CompatSummaryCard({ narration, personA, personB, checkId }) {
    const verdictLabel = narration.verdict_label || 'Has Its Challenges';
    const cards = narration.cards || {};
    const tone = _verdictTone(verdictLabel);
    const cardUrl = `${API}/compatibility/${checkId}/card`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('ac_token') : null;
    const [busy, setBusy] = useState(false);

    // Fetch the server-rendered PNG greeting card (1080×1350 portrait,
    // website colour palette). Returns a File the Web Share API and
    // download flow can both consume.
    const fetchCardFile = async () => {
        const resp = await fetch(cardUrl, { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error('card_fetch_failed');
        const blob = await resp.blob();
        const safeName = `astroconsult-${personA}-${personB}.png`.replace(/\s+/g, '-');
        return new File([blob], safeName, { type: 'image/png' });
    };

    const handleDownload = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const file = await fetchCardFile();
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Greeting card saved');
        } catch (e) {
            toast.error('Could not download the card image');
        } finally {
            setBusy(false);
        }
    };

    const handleShare = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const file = await fetchCardFile();
            // Mobile / PWA — native share sheet with the PNG attached
            // (WhatsApp picks it up as a sharable image).
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: `${personA} & ${personB} — KP Compatibility`,
                    text: `${verdictLabel} — via AstroConsult`,
                    files: [file],
                });
                return;
            }
            // Desktop fallback — download the PNG then open WhatsApp Web
            // with a short caption (user attaches the downloaded file).
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            const caption = `${personA} & ${personB} — ${verdictLabel}. AstroConsult KP Compatibility reading. https://astroconsult.app`;
            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(caption)}`, '_blank');
            toast.success('Card downloaded — attach it in WhatsApp');
        } catch (e) {
            if ((e?.name || '') !== 'AbortError') {
                toast.error('Could not share the card');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            className="bg-elevated border-marigold/15 rounded-sm overflow-hidden"
            data-testid="compat-summary-card"
        >
            <div className="p-5 md:p-6 space-y-5">
                <div>
                    <div className="overline text-marigold/80 mb-1">Share this reading</div>
                    <div className="text-xs text-slate-400">
                        A beautifully designed greeting card with your verdict + 5 layers — ready to share on WhatsApp or Instagram.
                    </div>
                </div>

                {/* Verdict */}
                <div className={`border ${tone} rounded-sm p-5 text-center`}>
                    <div className="overline opacity-80 mb-1">Verdict</div>
                    <div className="font-serif text-2xl md:text-3xl">{verdictLabel}</div>
                    <div className="text-sm mt-2 opacity-80">{personA} <span className="opacity-50 mx-1">&</span> {personB}</div>
                </div>

                {/* 5 layers — full titles + full descriptions */}
                <ul className="divide-y divide-marigold/10 border border-marigold/10 rounded-sm">
                    {LAYER_DEFAULTS.map(({ id, title: defaultTitle }) => {
                        const c = cards[id];
                        if (!c) return null;
                        const ic = (c.icon || 'mixed').toLowerCase();
                        const title = c.title || defaultTitle;
                        const desc = (c.description || '').trim();
                        return (
                            <li
                                key={id}
                                className="flex items-start gap-3 px-4 py-4"
                                data-testid={`compat-summary-${id}`}
                            >
                                <div className="shrink-0 mt-0.5">
                                    <CompatIconBadge icon={ic} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-marigold text-base">{title}</div>
                                    {desc && (
                                        <div className="text-sm text-parchment/85 leading-relaxed mt-1 whitespace-pre-line">
                                            {desc}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        onClick={handleDownload}
                        disabled={busy}
                        className="flex-1 bg-marigold/15 hover:bg-marigold/25 text-marigold border border-marigold/30 rounded-sm h-11 disabled:opacity-50"
                        data-testid="compat-summary-download-btn"
                    >
                        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
                        Download card
                    </Button>
                    <Button
                        onClick={handleShare}
                        disabled={busy}
                        className="flex-1 bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-11 disabled:opacity-50"
                        data-testid="compat-summary-share-btn"
                    >
                        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Share2 size={16} className="mr-2" />}
                        Share on WhatsApp
                    </Button>
                </div>
            </div>
        </Card>
    );
}


// Round 43d — Old PNG-based <CompatShareCard> is no longer rendered (the
// new <CompatSummaryCard> above replaces it). Kept the function definition
// removed entirely to drop the now-dead /compatibility/{id}/card fetch and
// blob handling.
